import { Megaphone, Clapperboard, PenLine, Video, Headset, Globe, MonitorSmartphone, UserCheck, ShieldCheck, XCircle, CheckCircle2, Clock, Building2, ArrowRight } from "lucide-react";

export const TEAM_ROLES = [
  { key: "ads", icon: Megaphone, t: "廣告投手" },
  { key: "creative", icon: Clapperboard, t: "素材製作" },
  { key: "editor", icon: PenLine, t: "社群小編" },
  { key: "kol", icon: Video, t: "網紅影片" },
  { key: "cs", icon: Headset, t: "客服到 23:00" },
  { key: "ota", icon: Globe, t: "OTA 代操" },
  { key: "pms", icon: MonitorSmartphone, t: "PMS 系統" },
  { key: "am", icon: UserCheck, t: "客戶經理" },
];

// 一列 = 一種花法：左標題、中間 56,000 的組成長條、右結論徽章
const BudgetRow = ({ testId, title, verdict, segments, badge, badgeTone, highlight, note }) => (
  <div
    data-testid={testId}
    className={`grid md:grid-cols-12 gap-3 md:gap-5 items-center rounded-2xl px-4 py-4 md:px-6 md:py-5 ${
      highlight ? "bg-[#0A2540] ring-2 ring-[#0D9488] shadow-xl" : "bg-white border border-slate-200"
    }`}
  >
    <div className="md:col-span-3">
      <div className={`text-xs font-bold mb-0.5 ${highlight ? "text-[#5EEAD4]" : "text-slate-400"}`}>{title}</div>
      <div className={`text-xl md:text-2xl font-black leading-tight ${highlight ? "text-white" : "text-slate-600"}`}>{verdict}</div>
    </div>

    <div className="md:col-span-7">
      <div className="flex h-11 md:h-12 rounded-xl overflow-hidden gap-[3px]">
        {segments.map((s) => (
          <div
            key={s.label}
            className={`flex items-center justify-center gap-1.5 text-[11px] md:text-xs font-bold px-1 ${s.cls}`}
            style={{ width: `${s.pct}%` }}
            title={s.label}
          >
            {s.Icon ? <s.Icon className="w-4 h-4 flex-shrink-0" /> : null}
            {s.text && <span className="truncate">{s.text}</span>}
          </div>
        ))}
      </div>
      {note && <div className={`text-[11px] md:text-xs mt-2 leading-snug ${highlight ? "text-white/80" : "text-slate-500"}`}>{note}</div>}
    </div>

    <div className="md:col-span-2 flex md:justify-end">
      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs md:text-sm font-black ${badgeTone}`}>
        {highlight ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
        {badge}
      </span>
    </div>
  </div>
);

export const TeamValueSection = () => (
  <div data-testid="team-value-section" className="mb-14 md:mb-20">
    <div className="text-center mb-8 md:mb-10">
      <div className="inline-flex items-center gap-2 bg-[#0D9488]/10 text-[#0D9488] px-4 py-2 rounded-full text-sm font-bold mb-4">
        <ShieldCheck className="w-4 h-4" />
        同樣 NT$ 56,000，差在哪？
      </div>
      <h3 className="text-2xl md:text-3xl font-black text-[#0A2540] leading-tight">
        用<span className="text-[#0D9488]">一位員工的薪水</span>，換一整個<span className="text-[#0D9488]">飯店集團等級的行銷部門</span>
      </h3>
    </div>

    <div className="space-y-3 md:space-y-4">
      <BudgetRow
        testId="team-value-hire"
        title="自己請 1 位行銷專員"
        verdict="只有 1 個人"
        segments={[
          { label: "薪資＋勞健保 51,000", pct: 91, text: "薪資＋勞健保 NT$ 51,000", cls: "bg-slate-200 text-slate-600" },
          { label: "廣告費 5,000", pct: 9, text: "5k", cls: "bg-red-100 text-red-500 min-w-[40px]" },
        ]}
        note="錢幾乎都付薪水，廣告預算只剩 5,000；1 個人要兼 8 個角色。"
        badge="缺 7 項沒人做"
        badgeTone="bg-red-50 text-red-500 border border-red-200"
      />
      <BudgetRow
        testId="team-value-agency"
        title="外包廣告代操"
        verdict="只有廣告"
        segments={[
          { label: "代操服務費 30,000", pct: 54, text: "代操服務費 NT$ 30,000", cls: "bg-slate-200 text-slate-600" },
          { label: "廣告費 26,000", pct: 46, text: "廣告費 NT$ 26,000", cls: "bg-slate-100 text-slate-500" },
        ]}
        note="廣告帶來詢問，但沒人拍影片、沒人接客服、沒人管 OTA 與系統。"
        badge="缺 6 項沒人做"
        badgeTone="bg-red-50 text-red-500 border border-red-200"
      />
      <BudgetRow
        testId="team-value-lakeze"
        highlight
        title="人客多成長方案（廣告費全包）"
        verdict="一整個行銷部門"
        segments={TEAM_ROLES.map((r) => ({ label: r.t, pct: 12.5, Icon: r.icon, cls: "bg-[#0D9488] text-white" }))}
        note={
          <span>
            {TEAM_ROLES.map((r) => r.t).join(" · ")}
            <span className="text-[#F5C24A] font-bold"> ＋ 48 隻影片、40 位以上 KOL</span>
          </span>
        }
        badge="8 項全包"
        badgeTone="bg-[#0D9488] text-white"
      />
    </div>

    <div className="mt-5 flex flex-col md:flex-row md:items-center md:justify-center gap-3 md:gap-8 text-sm text-[#475569]">
      <div className="flex items-center gap-2" data-testid="team-value-daily">
        <Clock className="w-4 h-4 text-[#0D9488] flex-shrink-0" />
        <span>換算一天 <span className="font-black text-[#0A2540]">NT$ 1,867</span> — 比一位正職日薪低，卻是一整個部門在上班</span>
      </div>
      <a href="#founder" data-testid="team-value-cred" className="flex items-center gap-2 hover:text-[#FF6B00] transition-colors">
        <Building2 className="w-4 h-4 text-[#FF6B00] flex-shrink-0" />
        <span>由自營旅宿、飯店集團出身的<span className="font-bold text-[#0A2540]">貝瑞大叔</span>帶隊</span>
        <ArrowRight className="w-4 h-4 text-[#FF6B00]" />
      </a>
    </div>
  </div>
);
